'use client';

import { useEffect, useMemo, useRef } from 'react';
import { useRouter } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { Plus } from 'lucide-react';
import { AuthTokenService } from '@/shared/lib/auth-token-service';
import { useAuthStore } from '@/features/auth';
import { trackEvent, AnalyticsEvent } from '@/shared/lib/analytics';
import { useProjects } from '@/features/project/hooks/use-projects';
import { useLastProject } from '@/shared/hooks/use-last-project';
import { NewProjectDialog, useNewProjectStore } from '@/features/project';
import { Button } from '@/shared/ui/shadcn/button';
import { LegalFooterLinks } from '@/shared/ui/LegalFooterLinks';

interface PageProps {
  params: Promise<Record<string, never>>;
  searchParams: Promise<Record<string, never>>;
}

/**
 * Root client resolver — the building page is the app home now (the Dashboard
 * was removed). For an authenticated user we resolve the active building
 * (last-opened → first) and redirect to `/project/{id}`. With no buildings we
 * show a first-run "create your first building" state. Anonymous visitors go
 * to `/auth/login`.
 *
 * Every branch renders the SAME markup the server rendered (a resolver screen
 * plus the legal footer), because auth lives in localStorage and the server
 * cannot know who is asking. A branch that renders visitor-specific content
 * here means the prerendered HTML is wrong for half the visitors and React
 * repaints it on hydration — that is what a public welcome screen on `/` did:
 * a signed-in user saw a "sign in / create account" page flash before the
 * spinner before the building.
 *
 * Paddle's domain-approval bot does not execute JS, so the server-rendered
 * HTML of every public page must carry the terms/privacy/pricing/refund links
 * as plain anchors — that is what LegalFooterLinks is for, here and on
 * `/auth/*`. Keep it in the SSR output; the bot never needed product copy.
 */
export default function Home(_props: PageProps) {
  const t = useTranslations('common');
  const tGrid = useTranslations('projectGrid');
  const tError = useTranslations('errorBoundary');
  const router = useRouter();
  const openNewProject = useNewProjectStore((s) => s.open);

  const hasToken =
    typeof window !== 'undefined' ? !!AuthTokenService.getTokenSync() : false;

  const isAuthSettled = useAuthStore((state) => state.isInitialized);
  const pendingEmailVerification = useAuthStore(
    (state) => state.pendingEmailVerification
  );

  // `/` lives OUTSIDE the (auth) route group, so ProtectedLayout's
  // verification gate never covered it. An unverified account landing here got
  // a 403 on the buildings list, which left `projects` undefined forever — not
  // an error state, just the redirect spinner below, spinning until the tab was
  // closed. Send them where every other protected screen sends them.
  useEffect(() => {
    if (isAuthSettled && pendingEmailVerification) {
      router.replace('/auth/verify-email');
    }
  }, [isAuthSettled, pendingEmailVerification, router]);

  // No token → the login page. Read synchronously above, so this fires on the
  // first client render; the markup below stays identical either way, so the
  // visitor sees one resolver screen and no intermediate page.
  // ADR-001: with no token the browser is tried as a sign-in method first,
  // so the login page is only the destination once that settles unsigned.
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const deviceSignInPending = useAuthStore((state) => state.deviceSignInPending);
  useEffect(() => {
    if (!hasToken && !deviceSignInPending && isAuthSettled && !isAuthenticated) {
      router.replace('/auth/login');
    }
  }, [hasToken, deviceSignInPending, isAuthSettled, isAuthenticated, router]);

  // Don't spend a request we know will 403. Before auth settles the local
  // snapshot is the only signal available, and using it here merely HOLDS the
  // fetch (never routes — see `isEmailUnverifiedSync`), so a stale flag costs
  // one render, not a redirect loop. Once settled, the server answer decides.
  const mayLoadProjects = isAuthSettled
    ? !pendingEmailVerification
    : !AuthTokenService.isEmailUnverifiedSync();

  const {
    data,
    isLoading,
    isError,
    refetch: refetchProjects,
  } = useProjects({ enabled: hasToken && mayLoadProjects });
  const projects = data?.data;
  const { lastProjectId } = useLastProject();

  const targetProjectId = useMemo(() => {
    if (!projects || projects.length === 0) return null;
    if (lastProjectId && projects.some((p) => p.id === lastProjectId)) {
      return lastProjectId;
    }
    return projects[0]?.id ?? null;
  }, [projects, lastProjectId]);

  useEffect(() => {
    if (hasToken && targetProjectId) {
      // Carry the query string through. `/` is a resolver, not a destination:
      // anything addressed to "the app" (`/?app-rating=2`, a campaign tag, a
      // deep-link param) arrives here and would be silently dropped on the way
      // to the building the user actually lands on.
      const query =
        typeof window !== 'undefined' ? window.location.search : '';
      router.replace(`/project/${targetProjectId}${query}`);
    }
  }, [hasToken, targetProjectId, router]);

  // Early-funnel: the empty buildings home (no projects yet) was shown. Fire
  // once per mount — the ref guards against re-fire on subsequent renders.
  const emptyStateTrackedRef = useRef(false);
  const showsEmptyState =
    hasToken && !isLoading && !!projects && projects.length === 0;
  useEffect(() => {
    if (showsEmptyState && !emptyStateTrackedRef.current) {
      emptyStateTrackedRef.current = true;
      trackEvent(AnalyticsEvent.HOME_EMPTY_STATE_VIEWED);
    }
  }, [showsEmptyState]);

  // Authenticated but no buildings yet → first-run create state.
  if (hasToken && !isLoading && projects && projects.length === 0) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 px-6 text-center">
        <Button onClick={openNewProject}>
          <Plus className="h-4 w-4" />
          {tGrid('createFirst')}
        </Button>
        <NewProjectDialog />
      </div>
    );
  }

  // The buildings list failed (offline, 5xx, a revoked token). Without this
  // branch every such failure landed on the redirect screen below and span
  // there forever, because the spinner's only exit is a resolved list.
  if (hasToken && isError) {
    return (
      <div className="flex min-h-screen flex-col">
        <div className="flex flex-1 items-center justify-center px-6">
          <div className="max-w-sm space-y-4 text-center">
            <p className="text-muted-foreground">{tError('global.title')}</p>
            <Button onClick={() => refetchProjects()}>
              {tError('global.tryAgain')}
            </Button>
          </div>
        </div>
        <LegalFooterLinks
          className="text-muted-foreground"
          linkClassName="hover:text-foreground"
        />
      </div>
    );
  }

  // Resolving: an authenticated visitor waiting for the target building, or an
  // anonymous one on the way to /auth/login. Same markup for both — and the
  // same markup the server prerendered.
  return (
    <div className="flex min-h-screen flex-col">
      <div className="flex flex-1 items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-foreground mx-auto"></div>
          <p className="mt-4 text-muted-foreground">{t('redirecting')}</p>
        </div>
      </div>
      <LegalFooterLinks
        className="text-muted-foreground"
        linkClassName="hover:text-foreground"
      />
    </div>
  );
}
